/**
 * AI智能助手面板
 * 支持4大智能检查功能：流程数据、章节内容、招标评分、废标项
 * 支持实时监控、手动检查、导出报告
 */
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { checkApi } from '../services/api';

// ==================== 类型定义 ====================

// 检查状态
type CheckStatus = 'not_checked' | 'pending' | 'checking' | 'passed' | 'warning' | 'error';

// 问题级别
type ProblemLevel = 'critical' | 'warning' | 'info';

// 单个检查项结果
interface CheckResult {
  status: CheckStatus;
  progress?: number; // 检查进度 0-100
  problemCount: number;
  checkedAt?: Date;
  subResults?: Record<string, { 
    status: CheckStatus; 
    problemCount: number;
    problems?: string[]; // 新增：问题列表
  }>; // 子项检查结果
}

// 问题详情
interface Problem {
  level: ProblemLevel;
  title: string;
  description: string;
  location?: {
    chapterId: string;
    chapterTitle: string;
  };
}

// 检查项配置
interface CheckItemConfig {
  id: string;
  title: string;
  icon?: string; // 可选：图标emoji
  description: string;
  critical?: boolean; // 是否为关键检查项
  subItems?: string[]; // 子检查项标题
}

// 组件Props
interface AIAssistantPanelProps {
  position: { x: number; y: number };
  onPositionChange: (pos: { x: number; y: number }) => void;
  onCollapse: () => void;
  projectId?: string; // 新增：项目ID，用于真实检查
  onScrollToSection?: (sectionId: string) => void; // 新增：滚动到指定章节的回调
  onScoreChange?: (score: number) => void; // 健康分数变化回调，用于同步折叠小球
}

// ==================== 检查项配置 ====================

const CHECK_ITEMS: CheckItemConfig[] = [
  {
    id: 'workflow',
    title: '流程数据检查',
    description: '检查项目配置、招标解析、分项报价、目录生成、文件生成',
    subItems: [
      '项目配置',
      '招标解析',
      '分项报价',
      '目录生成',
      '文件生成',
    ],
  },
  {
    id: 'content',
    title: '章节内容检查',
    description: '检查未生成章节、留白填写项、字数要求等',
    subItems: [
      '字数达标检查',
      '留白内容检查',
      '错别字检查',
      '逻辑连贯性检查',
      '关键要点覆盖检查',
      '专业术语检查',
      '格式规范检查',
    ],
  },
  {
    id: 'scoring',
    title: '招标评分检查',
    description: '检查评分项完整性、符合度、关键得分点，并预估得分',
    subItems: [
      '评分项完整性',
      '评分标准符合度',
      '关键得分点',
      '得分提升建议',
      '综合得分预估',
    ],
  },
  {
    id: 'disqualification',
    title: '废标项检查',
    description: 'AI对照废标条款，扫描投标文件中的废标风险',
    subItems: [
      '废标项风险扫描',
    ],
  },
];

const CHECK_ORDER = CHECK_ITEMS.map((item) => item.id);

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const simulateCheckOutcome = (id: string): { status: CheckStatus; problemCount: number } => {
  // TODO: 后续用真实检查结果替换这里的模拟逻辑
  switch (id) {
    case 'content':
      return { status: 'warning', problemCount: 2 };
    case 'typo':
      return { status: 'warning', problemCount: 3 };
    case 'scoring':
      return { status: 'warning', problemCount: 1 };
    default:
      return { status: 'passed', problemCount: 0 };
  }
};

const simulateSubCheckOutcome = (
  itemId: string,
  subTitle: string,
): { status: CheckStatus; problemCount: number } => {
  // TODO: 后续用真实子项检查结果替换这里的模拟逻辑
  // 先做一个可预期的示例分布，方便你感受效果
  if (itemId === 'workflow') {
    if (subTitle.includes('项目配置')) return { status: 'passed', problemCount: 0 };
    if (subTitle.includes('招标解析')) return { status: 'warning', problemCount: 1 };
    if (subTitle.includes('分项报价')) return { status: 'passed', problemCount: 0 };
    if (subTitle.includes('目录生成')) return { status: 'warning', problemCount: 2 };
    if (subTitle.includes('文件生成')) return { status: 'passed', problemCount: 0 };
  }

  if (itemId === 'content') {
    if (subTitle.includes('未生成章节')) return { status: 'warning', problemCount: 1 };
    if (subTitle.includes('空白占位')) return { status: 'warning', problemCount: 1 };
    return { status: 'passed', problemCount: 0 };
  }

  if (itemId === 'typo' && subTitle.includes('错别字')) {
    return { status: 'warning', problemCount: 2 };
  }

  // 默认：通过，无问题
  return { status: 'passed', problemCount: 0 };
};

// 获取问题详情清单（模拟数据）
const getProblemDetails = (itemId: string, subTitle: string): string[] => {
  // TODO: 后续替换为真实的问题详惁数据
  if (itemId === 'workflow') {
    if (subTitle.includes('招标解析')) {
      return ['缺少招标文件的项目概述解析'];
    }
    if (subTitle.includes('目录生成')) {
      return [
        '第3章节缺少必要的子节点',
        '目录结构与招标要求不一致'
      ];
    }
  }

  if (itemId === 'content') {
    if (subTitle.includes('未生成章节')) {
      return ['第5章第2节尚未生成内容'];
    }
    if (subTitle.includes('空白占位')) {
      return ['第7章第1节存在空白占位符'];
    }
  }

  if (itemId === 'typo' && subTitle.includes('错别字')) {
    return [
      '第3章存在错别字：“保证”应为“保障”',
      '第8章存在错别字：“制度”应为“制度”'
    ];
  }

  return ['暂无详细问题信息'];
};

// 真实的流程数据检查（调用后端API）
const realWorkflowCheck = async (
  projectId: string,
  subTitle: string
): Promise<{ status: CheckStatus; problemCount: number; problems: string[] }> => {
  // 子项映射
  const subItemMap: Record<string, string> = {
    '项目配置': 'project_config',
    '招标解析': 'tender_analysis',
    '分项报价': 'item_quotation',
    '目录生成': 'outline_generation',
    '文件生成': 'content_generation',
  };

  const subItem = subItemMap[subTitle];
  if (!subItem) {
    return { status: 'error', problemCount: 1, problems: ['未知的检查项'] };
  }

  try {
    const response = await checkApi.checkWorkflowSubItem(projectId, subItem);
    return {
      status: response.data.status as CheckStatus,
      problemCount: response.data.problem_count || 0,
      problems: response.data.problems || []
    };
  } catch (error: any) {
    console.error('检查失败:', error);
    return {
      status: 'error',
      problemCount: 1,
      problems: [error.response?.data?.detail || '检查失败']
    };
  }
};

// 章节内容全量检查（每章一次LLM，同时返回所有子项结果）
const realContentCheckAll = async (
  projectId: string
): Promise<Record<string, { status: CheckStatus; problemCount: number; problems: string[] }>> => {
  const keyToTitle: Record<string, string> = {
    word_count: '字数达标检查',
    empty_content: '留白内容检查',
    typos: '错别字检查',
    logic_coherence: '逻辑连贯性检查',
    key_points: '关键要点覆盖检查',
    terminology: '专业术语检查',
    format: '格式规范检查',
  };
  try {
    const response = await checkApi.checkContentAll(projectId);
    const data = response.data;
    const result: Record<string, { status: CheckStatus; problemCount: number; problems: string[] }> = {};
    for (const [key, title] of Object.entries(keyToTitle)) {
      const item = data[key];
      if (item) {
        result[title] = {
          status: item.status as CheckStatus,
          problemCount: item.problem_count || 0,
          problems: item.problems || [],
        };
      }
    }
    return result;
  } catch (error: any) {
    console.error('章节内容全量检查失败:', error);
    const errMsg = error.response?.data?.detail || '检查失败';
    const isTokenError = errMsg.includes('Token') &&
        (errMsg.includes('欠费') || errMsg.includes('不足') || errMsg.includes('额度'));
    if (isTokenError) {
      window.dispatchEvent(new CustomEvent('token:overdraft', { detail: { message: errMsg } }));
    }
    const errItem = {
      status: 'error' as CheckStatus,
      problemCount: 1,
      problems: [isTokenError ? 'Token余额不足，请先充値' : errMsg],
    };
    const result: Record<string, { status: CheckStatus; problemCount: number; problems: string[] }> = {};
    Object.values(keyToTitle).forEach(title => { result[title] = errItem; });
    return result;
  }
};

// 招标评分全量检查 - 一次API返回5个子项结果
const realScoringCheckAll = async (
  projectId: string
): Promise<Record<string, { status: CheckStatus; problemCount: number; problems: string[] }>> => {
  const keyToTitle: Record<string, string> = {
    scoring_completeness: '评分项完整性',
    scoring_compliance: '评分标准符合度',
    key_scoring_points: '关键得分点',
    score_improve: '得分提升建议',
    score_estimate: '综合得分预估',
  };
  try {
    const response = await checkApi.checkScoringAll(projectId);
    const data = response.data;
    const result: Record<string, { status: CheckStatus; problemCount: number; problems: string[] }> = {};
    for (const [key, title] of Object.entries(keyToTitle)) {
      const item = data[key];
      if (item) {
        result[title] = {
          status: item.status as CheckStatus,
          problemCount: item.problem_count || 0,
          problems: item.problems || [],
        };
      }
    }
    return result;
  } catch (error: any) {
    console.error('招标评分全量检查失败:', error);
    const errMsg = error.response?.data?.detail || '检查失败';
    const isTokenError = errMsg.includes('Token') &&
        (errMsg.includes('欠费') || errMsg.includes('不足') || errMsg.includes('额度'));
    if (isTokenError) {
      window.dispatchEvent(new CustomEvent('token:overdraft', { detail: { message: errMsg } }));
    }
    const errItem = {
      status: 'error' as CheckStatus,
      problemCount: 1,
      problems: [isTokenError ? 'Token余额不足，请先充値' : errMsg],
    };
    const result: Record<string, { status: CheckStatus; problemCount: number; problems: string[] }> = {};
    Object.values(keyToTitle).forEach(title => { result[title] = errItem; });
    return result;
  }
};

// 章节内容单个子项检查（手动单击子项时使用）
const realContentCheck = async (
  projectId: string,
  subTitle: string
): Promise<{ status: CheckStatus; problemCount: number; problems: string[] }> => {
  // 子项映射
  const subItemMap: Record<string, string> = {
    '字数达标检查': 'word_count',
    '留白内容检查': 'empty_content',
    '错别字检查': 'typos',
    '逻辑连贯性检查': 'logic_coherence',
    '关键要点覆盖检查': 'key_points',
    '专业术语检查': 'terminology',
    '格式规范检查': 'format',
  };

  const subItem = subItemMap[subTitle];
  if (!subItem) {
    return { status: 'error', problemCount: 1, problems: ['未知的检查项'] };
  }

  try {
    const response = await checkApi.checkContentSubItem(projectId, subItem);
    return {
      status: response.data.status as CheckStatus,
      problemCount: response.data.problem_count || 0,
      problems: response.data.problems || []
    };
  } catch (error: any) {
    console.error('检查失败:', error);
    const errMsg = error.response?.data?.detail || '检查失败';
    const isTokenError = errMsg.includes('Token') &&
        (errMsg.includes('欠费') || errMsg.includes('不足') || errMsg.includes('额度'));
    if (isTokenError) {
      window.dispatchEvent(new CustomEvent('token:overdraft', { detail: { message: errMsg } }));
    }
    return {
      status: 'error',
      problemCount: 1,
      problems: [isTokenError ? 'Token余额不足，请先充値' : errMsg]
    };
  }
};

// 真实的招标评分检查（调用后端API）
const realScoringCheck = async (
  projectId: string,
  subTitle: string
): Promise<{ status: CheckStatus; problemCount: number; problems: string[] }> => {
  // 子项映射
  const subItemMap: Record<string, string> = {
    '评分项完整性': 'scoring_completeness',
    '评分标准符合度': 'scoring_compliance',
    '关键得分点': 'key_scoring_points',
    '得分提升建议': 'score_improve',
    '综合得分预估': 'score_estimate',
  };

  const subItem = subItemMap[subTitle];
  if (!subItem) {
    return { status: 'error', problemCount: 1, problems: ['未知的检查项'] };
  }

  try {
    const response = await checkApi.checkScoringSubItem(projectId, subItem);
    return {
      status: response.data.status as CheckStatus,
      problemCount: response.data.problem_count || 0,
      problems: response.data.problems || []
    };
  } catch (error: any) {
    console.error('检查失败:', error);
    const errMsg = error.response?.data?.detail || '检查失败';
    const isTokenError = errMsg.includes('Token') &&
        (errMsg.includes('欠费') || errMsg.includes('不足') || errMsg.includes('额度'));
    if (isTokenError) {
      window.dispatchEvent(new CustomEvent('token:overdraft', { detail: { message: errMsg } }));
    }
    return {
      status: 'error',
      problemCount: 1,
      problems: [isTokenError ? 'Token余额不足，请先充値' : errMsg]
    };
  }
};

// 真实的废标项风险扫描（调用后端 AI检查）
const realDisqualificationCheck = async (
  projectId: string
): Promise<{ status: CheckStatus; problemCount: number; problems: string[] }> => {
  try {
    const response = await checkApi.checkDisqualification(projectId);
    return {
      status: response.data.status as CheckStatus,
      problemCount: response.data.problem_count || 0,
      problems: response.data.problems || [],
    };
  } catch (error: any) {
    console.error('废标项检查失败:', error);
    const errMsg = error.response?.data?.detail || '废标项风险扫描失败';
    const isTokenError = errMsg.includes('Token') &&
        (errMsg.includes('欠费') || errMsg.includes('不足') || errMsg.includes('额度'));
    if (isTokenError) {
      window.dispatchEvent(new CustomEvent('token:overdraft', { detail: { message: errMsg } }));
    }
    return {
      status: 'error',
      problemCount: 1,
      problems: [isTokenError ? 'Token余额不足，请先充値' : errMsg],
    };
  }
};

// ==================== 工具函数 ====================

// 获取状态显示信息
const getStatusDisplay = (status: CheckStatus, problemCount: number) => {
  switch (status) {
    case 'not_checked':
      return { text: '未检查', color: 'text-gray-500', bgColor: 'bg-gray-100' };
    case 'pending':
      return { text: '等待检查', color: 'text-gray-500', bgColor: 'bg-gray-100' };
    case 'checking':
      return { text: '检查中...', color: 'text-blue-500', bgColor: 'bg-blue-50' };
    case 'passed':
      return { text: '通过', color: 'text-green-500', bgColor: 'bg-green-50' };
    case 'warning':
      return { text: `${problemCount}个问题`, color: 'text-yellow-600', bgColor: 'bg-yellow-50' };
    case 'error':
      return { text: `${problemCount}个问题`, color: 'text-red-600', bgColor: 'bg-red-50' };
    default:
      return { text: '未知', color: 'text-gray-500', bgColor: 'bg-gray-100' };
  }
};

// ==================== 子组件 ====================

// 健康度仪表盘 - 360风格圆形进度圈（左右布局优化版）
const HealthDashboard: React.FC<{
  score: number;
  stats: { passed: number; warning: number; error: number };
  onCheckAll: () => void;
  onExportReport: () => void;
  onStopCheck: () => void; // 新增停止检查回调
  isChecking: boolean;
}> = ({ score, stats, onCheckAll, onExportReport, onStopCheck, isChecking }) => {
  // 根据分数决定颜色
  const getScoreColor = (score: number) => {
    if (score >= 85) return { color: '#10b981', label: '健康' }; // 绿色
    if (score >= 60) return { color: '#f97316', label: '一般' }; // 橙色
    return { color: '#ef4444', label: '风险' }; // 红色
  };

  const { color, label } = getScoreColor(score);
  const circumference = 2 * Math.PI * 50; // 半径50的圆周长
  const offset = circumference - (score / 100) * circumference;

  return (
    <div className="pl-4 pr-8 py-4 border-b border-gray-200 bg-gradient-to-br from-blue-50 to-white">
      <div className="flex items-center justify-between gap-4">
        {/* 左侧：360风格圆形进度圈 */}
        <div className={`relative w-24 h-24 flex-shrink-0 ${isChecking ? 'animate-pulse' : ''}`}>
          <svg className="w-full h-full transform -rotate-90">
            {/* 背景圆环 */}
            <circle
              cx="48"
              cy="48"
              r="40"
              stroke="#e5e7eb"
              strokeWidth="12"
              fill="none"
            />
            {/* 进度圆环 */}
            <circle
              cx="48"
              cy="48"
              r="40"
              stroke={color}
              strokeWidth="12"
              fill="none"
              strokeDasharray={circumference}
              strokeDashoffset={offset}
              strokeLinecap="round"
              className="transition-all duration-1000 ease-out"
            />
          </svg>
          {/* 中心内容：检查中显示文案，空闲显示分数 */}
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            {isChecking ? (
              <div className="text-sm font-medium text-gray-700">检查中</div>
            ) : (
              <>
                <div className="text-2xl font-bold" style={{ color }}>{score}</div>
                <div className="text-xs text-gray-500">{label}</div>
              </>
            )}
          </div>
        </div>

        {/* 右侧：状态统计 + 操作按钮 */}
        <div className="flex-1 flex flex-col gap-3">
          {/* 上半部分：三个状态卡片（横向） */}
          <div className="flex gap-2">
            <div className="flex-1 min-w-0 flex items-center justify-between px-2 py-1 bg-green-50 rounded">
              <span className="text-xs text-green-700 whitespace-nowrap">通过</span>
              <span className="text-sm font-semibold text-green-600 ml-1">{stats.passed}</span>
            </div>
            <div className="flex-1 min-w-0 flex items-center justify-between px-2 py-1 bg-yellow-50 rounded">
              <span className="text-xs text-yellow-700 whitespace-nowrap">警告</span>
              <span className="text-sm font-semibold text-yellow-600 ml-1">{stats.warning}</span>
            </div>
            <div className="flex-1 min-w-0 flex items-center justify-between px-2 py-1 bg-red-50 rounded">
              <span className="text-xs text-red-700 whitespace-nowrap">错误</span>
              <span className="text-sm font-semibold text-red-600 ml-1">{stats.error}</span>
            </div>
          </div>

          {/* 状态与按钮之间的装饰分割线 */}
          <div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent rounded-full" />

          {/* 下半部分：组合按钮：左侧全面检查 + 右侧导出图标 */}
          <div className="flex items-stretch text-xs font-medium gap-0">
            <button
              onClick={onCheckAll}
              disabled={isChecking}
              className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 bg-gradient-to-r from-blue-500 to-cyan-500 text-white hover:from-blue-600 hover:to-cyan-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed rounded-md rounded-r-none"
            >
              <span>{isChecking ? '检查中...' : '全面检查'}</span>
            </button>
            <button
              onClick={isChecking ? onStopCheck : onExportReport}
              className={`flex items-center justify-center px-2 py-1.5 transition-colors rounded-md rounded-l-none ${
                isChecking
                  ? 'bg-red-500 text-white hover:bg-red-600 border border-l-0 border-red-500'
                  : 'bg-white border border-l-0 border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white'
              }`}
              title={isChecking ? '停止检查' : '导出检查报告'}
            >
              {isChecking ? (
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <rect x="6" y="6" width="12" height="12" rx="1" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 7H16a4 4 0 010 8h-3m-4 0l3 3m0 0l3-3m-3 3V10"
                  />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// 检查项卡片
const CheckItemCard: React.FC<{
  config: CheckItemConfig;
  result: CheckResult;
  onCheck: () => void;
  onSubCheck?: (subTitle: string) => void;
  isExpanded: boolean;
  onToggleExpand: () => void;
  isRealTimeMonitoring?: boolean; // 是否实时监控中
  onShowProblemDetails?: (title: string, problems: string[]) => void; // 显示问题详情
  checkingNote?: string | null; // 检查中附加提示（如剩余章节数）
}> = ({ config, result, onCheck, onSubCheck, isExpanded, onToggleExpand, isRealTimeMonitoring, onShowProblemDetails, checkingNote }) => {
  const statusDisplay = getStatusDisplay(result.status, result.problemCount);

  return (
    <div className="border-b border-gray-200 last:border-b-0">
      {/* 卡片头部 */}
      <div
        className="px-4 py-2.5 hover:bg-blue-50 cursor-pointer transition-colors"
        onClick={onToggleExpand}
      >
        <div className="flex items-center justify-between">

          <div className="flex items-center gap-2 flex-1">
            <button className="text-gray-400 hover:text-gray-600">
              {isExpanded ? (
                <ChevronDownIcon className="w-4 h-4" />
              ) : (
                <ChevronRightIcon className="w-4 h-4" />
              )}
            </button>
            <span className="text-base">{config.icon}</span>
            <div className="flex items-center gap-2 flex-1">
              <span className="font-medium text-gray-900 text-sm">{config.title}</span>
              {config.critical && (
                <span className="text-xs px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-medium">
                  重要
                </span>
              )}
              {/* 实时监控指示器 */}
              {isRealTimeMonitoring && (
                <div className="relative" title="实时监控中">
                  <svg className="w-4 h-4 text-green-500 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
              )}
            </div>
          </div>

          {/* 状态指示器 */}
          {result.status === 'pending' ? (
            <div
              className={`px-2 py-1 rounded-full text-xs font-medium ${statusDisplay.bgColor} ${statusDisplay.color}`}
            >
              {statusDisplay.text}
            </div>
          ) : (
            <button
              type="button"
              className={`px-2 py-1 rounded-full text-xs font-medium group ${statusDisplay.bgColor} ${
                result.status === 'checking'
                  ? statusDisplay.color
                  : 'text-gray-700 hover:bg-blue-50 cursor-pointer'
              }`}
              onClick={(e) => {
                if (result.status === 'checking') return;
                e.stopPropagation();
                onCheck();
              }}
            >
              <span className={result.status === 'checking' ? '' : 'group-hover:hidden'}>
                {result.status === 'checking' && checkingNote
                  ? `检查中...${checkingNote}`
                  : statusDisplay.text}
              </span>
              {result.status !== 'checking' && (
                <span className="hidden group-hover:inline text-blue-600">
                  {result.status === 'not_checked' ? '开始检查' : '重写检查'}
                </span>
              )}
            </button>
          )}
        </div>
      </div>

      {/* 卡片内容（展开时显示） */}
      {isExpanded && (
        <div className="pl-10 pr-4 py-3 bg-gray-100/60 border-t border-gray-200">
          {config.subItems && config.subItems.length > 0 && (
            <div className="space-y-1">
              {config.subItems.map((subTitle) => {
                const subResult = result.subResults?.[subTitle];
                const subStatus = subResult?.status ?? 'not_checked';
                const subProblems = subResult?.problemCount ?? 0;

                let subStatusText = '未检查';
                let subStatusColor = 'text-gray-400';
                let isScoreEstimate = subTitle === '综合得分预估'; // 特殊标记

                if (subStatus === 'pending') {
                  subStatusText = '等待检查';
                  subStatusColor = 'text-gray-400';
                } else if (subStatus === 'checking') {
                  subStatusText = '检查中';
                  subStatusColor = 'text-blue-500';
                } else if (subStatus === 'passed' || subStatus === 'warning' || subStatus === 'error') {
                  // 综合得分预估：根据problems内容判断颜色
                  if (isScoreEstimate && subResult?.problems && subResult.problems.length > 0) {
                    // 从第一行提取总体得分率
                    const firstLine = subResult.problems[0] || '';
                    const scoreMatch = firstLine.match(/得分率[：:]\s*(\d+)%/);
                    const scoreRate = scoreMatch ? parseInt(scoreMatch[1]) : 0;
                    
                    if (scoreRate >= 75) {
                      subStatusText = '查看';
                      subStatusColor = 'text-green-500';
                    } else if (scoreRate >= 50) {
                      subStatusText = '查看';
                      subStatusColor = 'text-yellow-600';
                    } else {
                      subStatusText = '查看';
                      subStatusColor = 'text-red-600';
                    }
                  } else if (subStatus === 'passed') {
                    subStatusText = '正常';
                    subStatusColor = 'text-green-500';
                  } else {
                    subStatusText = `${subProblems}项问题`;
                    subStatusColor = 'text-yellow-600';
                  }
                }

                const isSubChecking = subStatus === 'checking';
                const isSubPending = subStatus === 'pending';
                const isSubNotChecked = subStatus === 'not_checked';
                const hasProblems = subProblems > 0 && (subStatus === 'warning' || subStatus === 'error');
                const isClickable = hasProblems || isScoreEstimate; // 综合得分预估也可点击

                return (
                  <div key={subTitle} className="flex items-center justify-between text-xs text-gray-600">
                    <span className="truncate mr-2">{subTitle}</span>
                    {isSubChecking ? (
                      <div className="flex items-center gap-1 text-[11px]">
                        <span className="inline-block w-3 h-3 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                        <span className="text-blue-500 text-[11px]">检查中</span>
                      </div>
                    ) : isSubPending ? (
                      <div className="flex items-center gap-1 text-[11px]">
                        <span className={subStatusColor}>{subStatusText}</span>
                      </div>
                    ) : isSubNotChecked ? (
                      <button
                        type="button"
                        className="flex items-center gap-1 text-[11px] cursor-pointer group"
                        onClick={(e) => {
                          if (!onSubCheck) return;
                          e.stopPropagation();
                          onSubCheck(subTitle);
                        }}
                      >
                        <span className={`${subStatusColor} group-hover:hidden`}>{subStatusText}</span>
                        <span className="hidden group-hover:inline text-blue-600">开始检查</span>
                      </button>
                    ) : isClickable ? (
                      <button
                        type="button"
                        className="flex items-center gap-1 text-[11px] cursor-pointer hover:underline"
                        onClick={(e) => {
                          e.stopPropagation();
                          // 打开问题详情弹窗
                          if (onShowProblemDetails) {
                            // 从 subResults 获取真实问题列表，如果没有则使用模拟数据
                            const problems = result?.subResults?.[subTitle]?.problems || getProblemDetails(config.id, subTitle);
                            onShowProblemDetails(
                              `${config.title} - ${subTitle}`,
                              problems
                            );
                          }
                        }}
                      >
                        <span className={subStatusColor}>{subStatusText}</span>
                      </button>
                    ) : (
                      <div className="flex items-center gap-1 text-[11px]">
                        <span className={subStatusColor}>{subStatusText}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// 操作栏 - 已移至顶部健康度仪表盘
// 保留此组件用于底部占位或未来扩展

// ==================== 主组件 ====================

const AIAssistantPanel: React.FC<AIAssistantPanelProps> = ({
  position,
  onPositionChange,
  onCollapse,
  projectId,
  onScrollToSection, // 新增：接收滚动回调
  onScoreChange, // 健康分数变化回调
}) => {
  // ========== 状态管理 ==========
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0, panelX: 0, panelY: 0 });

  // 实时监控（仅流程数据检查）
  const [isWorkflowMonitoring, setIsWorkflowMonitoring] = useState(true); // 默认开启流程数据实时监控
  const [lastFullCheckTime, setLastFullCheckTime] = useState<Date>();

  // 检查结果
  const [checkResults, setCheckResults] = useState<Record<string, CheckResult>>({
    workflow: {
      status: 'not_checked',
      problemCount: 0,
      subResults: {
        '项目配置': { status: 'not_checked', problemCount: 0 },
        '招标解析': { status: 'not_checked', problemCount: 0 },
        '分项报价': { status: 'not_checked', problemCount: 0 },
        '目录生成': { status: 'not_checked', problemCount: 0 },
        '文件生成': { status: 'not_checked', problemCount: 0 },
      },
    },
    content: {
      status: 'not_checked',
      problemCount: 0,
      subResults: {
        '未生成章节': { status: 'not_checked', problemCount: 0 },
        '空白占位内容': { status: 'not_checked', problemCount: 0 },
        '字数达标检查': { status: 'not_checked', problemCount: 0 },
        '内容空洞与重复': { status: 'not_checked', problemCount: 0 },
        '章节逻辑连贯性': { status: 'not_checked', problemCount: 0 },
        '关键要点覆盖': { status: 'not_checked', problemCount: 0 },
        '专业术语规范': { status: 'not_checked', problemCount: 0 },
        '章节格式规范': { status: 'not_checked', problemCount: 0 },
      },
    },
    scoring: {
      status: 'not_checked',
      problemCount: 0,
    },
    typo: {
      status: 'not_checked',
      problemCount: 0,
    },
    disqualification: {
      status: 'not_checked',
      problemCount: 0,
    },
  });

  // 健康度和统计：由 checkResults 自动计算（保证与下方实时同步）
  const checkStats = useMemo(() => {
    let passed = 0, warning = 0, error = 0;
    for (const item of Object.values(checkResults)) {
      if (item.subResults && Object.keys(item.subResults).length > 0) {
        // 始终从子项实时汇总（子项完成就即时计入头部统计）
        for (const sub of Object.values(item.subResults)) {
          if (sub.status === 'warning') warning += sub.problemCount || 0;
          else if (sub.status === 'error') error += sub.problemCount || 0;
        }
        // 父项已全部完成且无问题则计通过
        if (item.status === 'passed') passed++;
      } else {
        // 无子项配置，直接读父项
        if (item.status === 'passed') passed++;
        else if (item.status === 'warning') warning += item.problemCount || 0;
        else if (item.status === 'error') error += item.problemCount || 0;
      }
    }
    return { passed, warning, error };
  }, [checkResults]);

  // 健康分数：满分100，子项warning扣problemCount*0.5，error扣problemCount*2，实时计算
  const healthScore = useMemo(() => {
    let score = 100;
    for (const item of Object.values(checkResults)) {
      if (item.subResults && Object.keys(item.subResults).length > 0) {
        // 始终从子项实时扣分（子项完成就即时反映）
        for (const sub of Object.values(item.subResults)) {
          if (sub.status === 'warning') score -= (sub.problemCount || 0) * 0.5;
          else if (sub.status === 'error') score -= (sub.problemCount || 0) * 2;
        }
      } else {
        if (item.status === 'warning') score -= (item.problemCount || 0) * 0.5;
        else if (item.status === 'error') score -= (item.problemCount || 0) * 2;
      }
    }
    return Math.max(0, Math.round(score));
  }, [checkResults]);

  // 健康分数变化时通知外部（折叠小球同步）
  useEffect(() => {
    onScoreChange?.(healthScore);
  }, [healthScore, onScoreChange]);

  // 组件挂载时立即同步一次分数（解决小球初始显示问题）
  useEffect(() => {
    // 延迟一次执行，确保 healthScore 已计算完成
    const timer = setTimeout(() => {
      onScoreChange?.(healthScore);
    }, 0);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 展开状态
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  const [isCheckingAll, setIsCheckingAll] = useState(false);
  const [shouldStopCheck, setShouldStopCheck] = useState(false); // 停止检查标志
  const [contentCheckNote, setContentCheckNote] = useState<string | null>(null); // 章节内容检查剩余章节数倒计
  const [scoringCheckNote, setScoringCheckNote] = useState<string | null>(null); // 招标评分检查剩余子项数倒计

  // 计算是否有任何检查正在进行
  const isAnyChecking = useMemo(() => {
    return Object.values(checkResults).some(result => result.status === 'checking');
  }, [checkResults]);

  // 问题详情弹窗状态
  const [problemDialog, setProblemDialog] = useState<{
    isOpen: boolean;
    title: string;
    problems: string[];
  }>({ isOpen: false, title: '', problems: [] });

  // ========== 拖动逻辑 ==========
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - dragStartRef.current.x;
      const deltaY = e.clientY - dragStartRef.current.y;

      const newX = Math.max(0, Math.min(window.innerWidth - 320, dragStartRef.current.panelX + deltaX));
      const newY = Math.max(0, Math.min(window.innerHeight - 100, dragStartRef.current.panelY + deltaY));

      onPositionChange({ x: newX, y: newY });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, onPositionChange]);

  // ========== 恢复检查结果 ==========
  useEffect(() => {
    // 当有projectId时，从数据库加载检查结果
    if (!projectId) return;

    const loadCheckResults = async () => {
      try {
        const response = await checkApi.getCheckResults(projectId);
        const savedResults = response.data;

        // 更新checkResults状态
        // 流程数据检查
        if (savedResults.workflow) {
          const workflowResults: Record<string, { status: CheckStatus; problemCount: number; problems?: string[] }> = {};
          
          Object.keys(savedResults.workflow).forEach(subItem => {
            const subResult = savedResults.workflow[subItem];
            workflowResults[subItem] = {
              status: subResult.status as CheckStatus,
              problemCount: subResult.problem_count,
              problems: subResult.problems
            };
          });

          // 计算父项状态
          const problemCount = Object.values(workflowResults).reduce((sum, r) => sum + r.problemCount, 0);
          const hasError = Object.values(workflowResults).some(r => r.status === 'error');
          const hasWarning = Object.values(workflowResults).some(r => r.status === 'warning');
          const parentStatus: CheckStatus = problemCount === 0 ? 'passed' : hasError ? 'error' : 'warning';

          setCheckResults(prev => ({
            ...prev,
            workflow: {
              status: parentStatus,
              problemCount: problemCount,
              checkedAt: new Date(),
              subResults: workflowResults
            }
          }));
        }
        
        // 章节内容检查
        if (savedResults.content) {
          const contentResults: Record<string, { status: CheckStatus; problemCount: number; problems?: string[] }> = {};
          
          Object.keys(savedResults.content).forEach(subItem => {
            const subResult = savedResults.content[subItem];
            contentResults[subItem] = {
              status: subResult.status as CheckStatus,
              problemCount: subResult.problem_count,
              problems: subResult.problems
            };
          });

          // 计算父项状态
          const problemCount = Object.values(contentResults).reduce((sum, r) => sum + r.problemCount, 0);
          const hasError = Object.values(contentResults).some(r => r.status === 'error');
          const hasWarning = Object.values(contentResults).some(r => r.status === 'warning');
          const parentStatus: CheckStatus = problemCount === 0 ? 'passed' : hasError ? 'error' : 'warning';

          setCheckResults(prev => ({
            ...prev,
            content: {
              status: parentStatus,
              problemCount: problemCount,
              checkedAt: new Date(),
              subResults: contentResults
            }
          }));
        }

        // 招标评分检查
        if (savedResults.scoring) {
          const scoringResults: Record<string, { status: CheckStatus; problemCount: number; problems?: string[] }> = {};

          Object.keys(savedResults.scoring).forEach(subItem => {
            const subResult = savedResults.scoring[subItem];
            scoringResults[subItem] = {
              status: subResult.status as CheckStatus,
              problemCount: subResult.problem_count,
              problems: subResult.problems
            };
          });

          const problemCount = Object.values(scoringResults).reduce((sum, r) => sum + r.problemCount, 0);
          const hasError = Object.values(scoringResults).some(r => r.status === 'error');
          const parentStatus: CheckStatus = problemCount === 0 ? 'passed' : hasError ? 'error' : 'warning';

          setCheckResults(prev => ({
            ...prev,
            scoring: {
              status: parentStatus,
              problemCount: problemCount,
              checkedAt: new Date(),
              subResults: scoringResults
            }
          }));
        }

        // 废标项检查
        if (savedResults.disqualification) {
          const disqResults: Record<string, { status: CheckStatus; problemCount: number; problems?: string[] }> = {};

          Object.keys(savedResults.disqualification).forEach(subItem => {
            const subResult = savedResults.disqualification[subItem];
            disqResults[subItem] = {
              status: subResult.status as CheckStatus,
              problemCount: subResult.problem_count,
              problems: subResult.problems
            };
          });

          const problemCount = Object.values(disqResults).reduce((sum, r) => sum + r.problemCount, 0);
          const hasError = Object.values(disqResults).some(r => r.status === 'error');
          const parentStatus: CheckStatus = problemCount === 0 ? 'passed' : hasError ? 'error' : 'warning';

          setCheckResults(prev => ({
            ...prev,
            disqualification: {
              status: parentStatus,
              problemCount: problemCount,
              checkedAt: new Date(),
              subResults: disqResults
            }
          }));
        }
      } catch (error) {
        console.error('加载检查结果失败:', error);
      }
    };

    loadCheckResults();
  }, [projectId]);

  // ========== 事件处理 ==========
  const handleStartDrag = (e: React.MouseEvent) => {
    setIsDragging(true);
    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      panelX: position.x,
      panelY: position.y,
    };
    e.preventDefault();
  };

  const handleToggleExpand = (itemId: string) => {
    setExpandedItems((prev) => {
      const newSet = new Set<string>();
      // 如果当前项已展开，则关闭它；否则展开当前项（关闭其他项）
      if (!prev.has(itemId)) {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const runItemCheck = async (itemId: string, options?: { updateStats?: boolean }) => {
    const { updateStats = true } = options || {};
    const itemConfig = CHECK_ITEMS.find((item) => item.id === itemId);
    if (!itemConfig) return;

    // 展开当前项
    setExpandedItems(new Set([itemId]));

    // 初始化当前项的子结果为等待检查
    setCheckResults((prev) => {
      const prevItem = prev[itemId] || { status: 'pending', problemCount: 0 };
      const subResults: Record<string, { status: CheckStatus; problemCount: number }> = {};
      if (itemConfig.subItems) {
        itemConfig.subItems.forEach((subTitle) => {
          const existing = prevItem.subResults?.[subTitle];
          subResults[subTitle] = existing || { status: 'pending', problemCount: 0 };
        });
      }
      return {
        ...prev,
        [itemId]: {
          ...prevItem,
          status: 'checking',
          problemCount: 0,
          progress: 0,
          subResults,
        },
      };
    });

    // 章节内容检查：批量模式（所有子项同时开始，一次API返回所有结果）
    if (itemId === 'content' && projectId) {
      // 所有子项同时置为 checking
      setCheckResults((prev) => {
        const prevItem = prev[itemId];
        if (!prevItem) return prev;
        const subResults: Record<string, { status: CheckStatus; problemCount: number }> = {};
        itemConfig.subItems?.forEach((subTitle) => {
          subResults[subTitle] = { status: 'checking', problemCount: prevItem.subResults?.[subTitle]?.problemCount ?? 0 };
        });
        return { ...prev, [itemId]: { ...prevItem, subResults } };
      });

      // 启动异步轮询进度，更新倒计显示
      let pollInterval: ReturnType<typeof setInterval> | null = null;
      const startProgressPoll = () => {
        pollInterval = setInterval(async () => {
          try {
            const res = await checkApi.getContentCheckProgress(projectId);
            const { total, remaining } = res.data as { total: number; remaining: number; running: boolean };
            if (total > 0) {
              setContentCheckNote(remaining > 0 ? String(remaining) : null);
            }
          } catch (_) {
            // 轮询失败静默志略
          }
        }, 1500);
      };
      startProgressPoll();

      try {
        const allResults = await realContentCheckAll(projectId);

        let totalProblems = 0;
        let hasErr = false;
        let hasWarn = false;
        for (const res of Object.values(allResults)) {
          totalProblems += res.problemCount;
          if (res.status === 'error') hasErr = true;
          else if (res.status === 'warning') hasWarn = true;
        }
        const contentFinalStatus: CheckStatus = totalProblems === 0 ? 'passed' : hasErr ? 'error' : 'warning';

        setCheckResults((prev) => {
          const prevItem = prev[itemId];
          if (!prevItem) return prev;
          return {
            ...prev,
            [itemId]: {
              ...prevItem,
              status: contentFinalStatus,
              problemCount: totalProblems,
              checkedAt: new Date(),
              subResults: allResults,
            },
          };
        });
      } finally {
        if (pollInterval) {
          clearInterval(pollInterval);
          pollInterval = null;
        }
        setContentCheckNote(null);
      }
      return;
    }

    // 招标评分检查：批量模式（所有子项并行运行，一次API返回所有结果）
    if (itemId === 'scoring' && projectId) {
      // 所有子项同时置为 checking
      setCheckResults((prev) => {
        const prevItem = prev[itemId];
        if (!prevItem) return prev;
        const subResults: Record<string, { status: CheckStatus; problemCount: number }> = {};
        itemConfig.subItems?.forEach((subTitle) => {
          subResults[subTitle] = { status: 'checking', problemCount: prevItem.subResults?.[subTitle]?.problemCount ?? 0 };
        });
        return { ...prev, [itemId]: { ...prevItem, subResults } };
      });

      // 启动异步轮询进度
      let scoringPollInterval: ReturnType<typeof setInterval> | null = null;
      const startScoringPoll = () => {
        scoringPollInterval = setInterval(async () => {
          try {
            const res = await checkApi.getScoringCheckProgress(projectId);
            const { total, remaining } = res.data as { total: number; remaining: number; running: boolean };
            if (total > 0) {
              setScoringCheckNote(remaining > 0 ? String(remaining) : null);
            }
          } catch (_) { /* 轮询失败静默忙略 */ }
        }, 1500);
      };
      startScoringPoll();

      try {
        const allResults = await realScoringCheckAll(projectId);

        let totalProblems = 0;
        let hasErr = false;
        let hasWarn = false;
        for (const res of Object.values(allResults)) {
          totalProblems += res.problemCount;
          if (res.status === 'error') hasErr = true;
          else if (res.status === 'warning') hasWarn = true;
        }
        const scoringFinalStatus: CheckStatus = totalProblems === 0 ? 'passed' : hasErr ? 'error' : 'warning';

        setCheckResults((prev) => {
          const prevItem = prev[itemId];
          if (!prevItem) return prev;
          return {
            ...prev,
            [itemId]: {
              ...prevItem,
              status: scoringFinalStatus,
              problemCount: totalProblems,
              checkedAt: new Date(),
              subResults: allResults,
            },
          };
        });
      } finally {
        if (scoringPollInterval) {
          clearInterval(scoringPollInterval);
          scoringPollInterval = null;
        }
        setScoringCheckNote(null);
      }
      return;
    }

    let aggregatedProblems = 0;
    let hasWarning = false;
    let hasError = false;

    if (itemConfig.subItems && itemConfig.subItems.length > 0) {
      for (const subTitle of itemConfig.subItems) {
        // 当前子项置为检查中
        setCheckResults((prev) => {
          const prevItem = prev[itemId];
          if (!prevItem) return prev;
          const prevSubResults = prevItem.subResults || {};
          return {
            ...prev,
            [itemId]: {
              ...prevItem,
              subResults: {
                ...prevSubResults,
                [subTitle]: {
                  status: 'checking',
                  problemCount: prevSubResults[subTitle]?.problemCount ?? 0,
                },
              },
            },
          };
        });

        // 每个子项 2 秒检查
        await sleep(2000);

        // 检查是否需要停止
        if (shouldStopCheck) {
          break;
        }

        // 调用真实检查（流程数据、章节内容、招标评分、废标项）或模拟检查
        let subOutcome: { status: CheckStatus; problemCount: number; problems?: string[] };
        if (itemId === 'workflow' && projectId) {
          // 流程数据检查 - 真实API
          subOutcome = await realWorkflowCheck(projectId, subTitle);
        } else if (itemId === 'content' && projectId) {
          // 章节内容检查 - 真实API
          subOutcome = await realContentCheck(projectId, subTitle);
        } else if (itemId === 'scoring' && projectId) {
          // 招标评分检查 - 真实API
          subOutcome = await realScoringCheck(projectId, subTitle);
        } else if (itemId === 'disqualification' && projectId) {
          // 废标项风险扫描 - 真实API
          subOutcome = await realDisqualificationCheck(projectId);
        } else {
          // 其他检查项 - 模拟检查
          subOutcome = simulateSubCheckOutcome(itemId, subTitle);
        }
        
        aggregatedProblems += subOutcome.problemCount;
        if (subOutcome.status === 'error') {
          hasError = true;
          hasWarning = true;
        } else if (subOutcome.status === 'warning') {
          hasWarning = true;
        }

        // 写回当前子项结果与累计问题数
        setCheckResults((prev) => {
          const prevItem = prev[itemId];
          if (!prevItem) return prev;
          const prevSubResults = prevItem.subResults || {};
          return {
            ...prev,
            [itemId]: {
              ...prevItem,
              problemCount: aggregatedProblems,
              subResults: {
                ...prevSubResults,
                [subTitle]: {
                  status: subOutcome.status,
                  problemCount: subOutcome.problemCount,
                  problems: subOutcome.problems // 保存问题列表
                },
              },
            },
          };
        });
      }
    }

    const finalStatus: CheckStatus =
      aggregatedProblems === 0 ? 'passed' : hasError ? 'error' : 'warning';

    // 更新父项最终状态
    setCheckResults((prev) => {
      const prevItem = prev[itemId];
      if (!prevItem) return prev;
      return {
        ...prev,
        [itemId]: {
          ...prevItem,
          status: finalStatus,
          problemCount: aggregatedProblems,
          checkedAt: new Date(),
        },
      };
    });

  };

  const runSubCheck = async (itemId: string, subTitle: string) => {
    const itemConfig = CHECK_ITEMS.find((item) => item.id === itemId);
    if (!itemConfig) return;

    // 标记当前子项为检查中
    setCheckResults((prev) => {
      const prevItem = prev[itemId];
      if (!prevItem) return prev;
      const prevSubResults = prevItem.subResults || {};
      return {
        ...prev,
        [itemId]: {
          ...prevItem,
          subResults: {
            ...prevSubResults,
            [subTitle]: {
              status: 'checking',
              problemCount: prevSubResults[subTitle]?.problemCount ?? 0,
            },
          },
        },
      };
    });

    // 2秒检查动画
    await sleep(2000);

    // 根据不同的itemId调用不同的检查函数
    let subOutcome: { status: CheckStatus; problemCount: number; problems?: string[] };
    
    if (itemId === 'workflow' && projectId) {
      // 流程数据检查 - 调用真实API
      subOutcome = await realWorkflowCheck(projectId, subTitle);
    } else if (itemId === 'content' && projectId) {
      // 章节内容检查 - 调用真实API
      subOutcome = await realContentCheck(projectId, subTitle);
    } else if (itemId === 'scoring' && projectId) {
      // 招标评分检查 - 调用真实API
      subOutcome = await realScoringCheck(projectId, subTitle);
    } else if (itemId === 'disqualification' && projectId) {
      // 废标项风险扫描 - 调用真实API
      subOutcome = await realDisqualificationCheck(projectId);
    } else {
      // 其他检查项 - 使用模拟数据
      subOutcome = simulateSubCheckOutcome(itemId, subTitle);
    }

    // 写回子项结果，并重新汇总该父项的问题总数和状态（不更新顶部统计）
    setCheckResults((prev) => {
      const prevItem = prev[itemId];
      if (!prevItem) return prev;
      const prevSubResults = prevItem.subResults || {};
      const newSubResults: Record<string, { status: CheckStatus; problemCount: number }> = {
        ...prevSubResults,
        [subTitle]: subOutcome,
      };

      let aggregatedProblems = 0;
      let hasWarning = false;
      let hasError = false;
      Object.values(newSubResults).forEach((sr) => {
        aggregatedProblems += sr.problemCount || 0;
        if (sr.status === 'error') {
          hasError = true;
          hasWarning = true;
        } else if (sr.status === 'warning') {
          hasWarning = true;
        }
      });

      const finalStatus: CheckStatus =
        aggregatedProblems === 0 ? 'passed' : hasError ? 'error' : 'warning';

      return {
        ...prev,
        [itemId]: {
          ...prevItem,
          status: finalStatus,
          problemCount: aggregatedProblems,
          subResults: newSubResults,
          checkedAt: new Date(),
        },
      };
    });
  };

  const handleCheck = (itemId: string) => {
    // 检查时把当前项状态改为 pending
    setCheckResults(prev => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        status: 'pending'
      }
    }));
    void runItemCheck(itemId, { updateStats: false });
  };

  const handleCheckAll = async () => {
    setIsCheckingAll(true);
    setShouldStopCheck(false); // 重置停止标志

    // 1. 重置所有检查项为等待状态（头部stats由useMemo自动计算，无需清零）
    setCheckResults(() => {
      const reset: Record<string, CheckResult> = {};
      for (const item of CHECK_ITEMS) {
        const subResults: Record<string, { status: CheckStatus; problemCount: number }> = {};
        if (item.subItems) {
          item.subItems.forEach((subTitle) => {
            subResults[subTitle] = { status: 'pending', problemCount: 0 };
          });
        }
        reset[item.id] = {
          status: 'pending',
          problemCount: 0,
          progress: 0,
          subResults,
        };
      }
      return reset;
    });

    // 3. 按顺序依次检查每个大项（内部再按子项 2 秒一个执行）
    for (const id of CHECK_ORDER) {
      // 检查是否需要停止
      if (shouldStopCheck) {
        break;
      }
      await runItemCheck(id, { updateStats: true });
    }

    // 4. 全部完成，收起所有检查项并关闭圈的动画
    setExpandedItems(new Set());
    setIsCheckingAll(false);
    setShouldStopCheck(false);
  };

  const handleExportReport = () => {
    // TODO: 实现导出报告逻辑
    alert('报告导出功能开发中...');
  };

  const handleStopCheck = () => {
    setShouldStopCheck(true);
    setIsCheckingAll(false);
  };

  // 流程数据检查实时监控逻辑（后续实现）
  // TODO: 实现流程数据实时监控功能

  // ========== 渲染 ==========
  return (
    <>
      {/* AI 智能助手面板 */}
      <div
        className="fixed w-80 bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/80 flex flex-col z-[300] max-h-[90vh]"
        style={{
          left: position.x,
          top: position.y,
        }}
      >
      {/* 顶部标题栏（拖动区域） */}
      <div
        className="px-4 py-3 border-b border-gray-200 flex items-center justify-between cursor-move select-none bg-gradient-to-r from-blue-500 to-cyan-500 rounded-t-2xl"
        onMouseDown={handleStartDrag}
      >
        <h3 className="font-semibold text-white flex items-center">
          <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
            />
          </svg>
          AI检查助手
        </h3>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onCollapse();
          }}
          onMouseDown={(e) => e.stopPropagation()}
          className="p-1 rounded-lg hover:bg-white/20 transition-colors text-white"
          title="收起"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {/* 健康度仪表盘（包含状态和按钮） */}
      <HealthDashboard
        score={healthScore}
        stats={checkStats}
        onCheckAll={handleCheckAll}
        onExportReport={handleExportReport}
        onStopCheck={handleStopCheck}
        isChecking={isAnyChecking}
      />

      {/* 检查项目列表（可滚动） */}
      <div className="flex-1 overflow-y-auto rounded-b-2xl">
        {CHECK_ITEMS.map((item) => (
          <CheckItemCard
            key={item.id}
            config={item}
            result={checkResults[item.id]}
            onCheck={() => handleCheck(item.id)}
            onSubCheck={(subTitle) => runSubCheck(item.id, subTitle)}
            isExpanded={expandedItems.has(item.id)}
            onToggleExpand={() => handleToggleExpand(item.id)}
            isRealTimeMonitoring={item.id === 'workflow' && isWorkflowMonitoring}
            checkingNote={item.id === 'content' ? contentCheckNote : item.id === 'scoring' ? scoringCheckNote : undefined}
            onShowProblemDetails={(title, problems) => {
              setProblemDialog({ isOpen: true, title, problems });
            }}
          />
        ))}
      </div>
    </div>

    {/* 问题详情弹窗（全屏区域） */}
    {problemDialog.isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center p-4"
          onClick={() => setProblemDialog({ isOpen: false, title: '', problems: [] })}
        >
          <div 
            className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* 头部 */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-blue-50 to-cyan-50">
              <h3 className="text-lg font-semibold text-gray-900">{problemDialog.title}</h3>
              <button
                onClick={() => setProblemDialog({ isOpen: false, title: '', problems: [] })}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* 问题列表 */}
            <div className="px-6 py-4 overflow-y-auto max-h-[calc(80vh-120px)]">
              {problemDialog.problems.length > 0 ? (
                <ul className="space-y-3">
                  {problemDialog.problems.map((problem, index) => {
                    // 解析问题字符串，提取章节ID
                    const match = problem.match(/^(.+?)\|outline_item:(\d+)$/);
                    const problemText = match ? match[1] : problem;
                    const outlineItemId = match ? match[2] : null;
                    
                    return (
                      <li key={index} className="flex items-start gap-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <span className="flex-shrink-0 w-6 h-6 bg-yellow-400 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                          {index + 1}
                        </span>
                        <span className="flex-1 text-sm text-gray-700">
                          {problemText}
                          {outlineItemId && (
                            <>
                              {' '}
                              <button
                                onClick={() => {
                                  // 直接调用ContentEdit传入的scrollToSection函数
                                  if (onScrollToSection) {
                                    onScrollToSection(outlineItemId);
                                    
                                    // 添加高亮效果
                                    setTimeout(() => {
                                      const element = document.getElementById(`section-${outlineItemId}`);
                                      if (element) {
                                        element.classList.add('ring-2', 'ring-blue-500', 'ring-offset-2');
                                        setTimeout(() => {
                                          element.classList.remove('ring-2', 'ring-blue-500', 'ring-offset-2');
                                        }, 2000);
                                      }
                                    }, 300); // 等待滚动开始
                                  } else {
                                    console.warn('没有传入onScrollToSection回调函数');
                                  }
                                }}
                                className="text-blue-600 hover:text-blue-800 hover:underline font-medium cursor-pointer"
                              >
                                [定位]
                              </button>
                            </>
                          )}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="text-sm text-gray-500 text-center py-8">暂无问题详情</p>
              )}
            </div>

            {/* 底部按钮 */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setProblemDialog({ isOpen: false, title: '', problems: [] })}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                知道了
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AIAssistantPanel;
